/**
 * Biblioteca de modelos de processo pré-configurados.
 * Cada modelo inclui etapas, funções e SLA padrão prontos para uso imediato.
 */

export interface ProcessTemplateStage {
  name: string;
  role: string;
  slaHours: number;
  description: string;
  canSkip: boolean;
  requireApproval: boolean;
}

export interface ProcessTemplate {
  id: string;
  category: ProcessTemplateCategory;
  name: string;
  description: string;
  icon: string;
  stages: ProcessTemplateStage[];
}

export type ProcessTemplateCategory =
  | 'financeiro'
  | 'compras'
  | 'rh'
  | 'operacional'
  | 'personalizado';

export const CATEGORY_LABELS: Record<ProcessTemplateCategory, string> = {
  financeiro: 'Financeiro',
  compras: 'Compras',
  rh: 'RH',
  operacional: 'Operacional',
  personalizado: 'Personalizado',
};

export const CATEGORY_ICONS: Record<ProcessTemplateCategory, string> = {
  financeiro: 'DollarSign',
  compras: 'ShoppingCart',
  rh: 'Users',
  operacional: 'Settings',
  personalizado: 'Puzzle',
};

export const PROCESS_TEMPLATES: ProcessTemplate[] = [
  // ── Financeiro ─────────────────────────────────────────
  {
    id: 'contas-a-pagar',
    category: 'financeiro',
    name: 'Contas a Pagar',
    description: 'Fluxo completo de pagamento: da solicitação à liquidação.',
    icon: 'DollarSign',
    stages: [
      {
        name: 'Receber solicitação',
        role: 'Solicitante',
        slaHours: 4,
        description: 'Registrar a despesa com nota fiscal e valor.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Validar documentação',
        role: 'Financeiro',
        slaHours: 8,
        description: 'Conferir nota fiscal, centro de custo e aprovação anterior.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Aprovar pagamento',
        role: 'Gestor',
        slaHours: 24,
        description: 'Aprovação do gestor responsável pelo centro de custo.',
        canSkip: false,
        requireApproval: true,
      },
      {
        name: 'Efetuar pagamento',
        role: 'Financeiro',
        slaHours: 8,
        description: 'Realizar transferência ou emitir boleto.',
        canSkip: false,
        requireApproval: false,
      },
    ],
  },

  // ── Compras ────────────────────────────────────────────
  {
    id: 'compras-padrao',
    category: 'compras',
    name: 'Processo de Compras',
    description: 'Da solicitação de compra até o recebimento do pedido.',
    icon: 'ShoppingCart',
    stages: [
      {
        name: 'Solicitação de compra',
        role: 'Solicitante',
        slaHours: 4,
        description: 'Preencher formulário com item, quantidade e justificativa.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Cotação',
        role: 'Compras',
        slaHours: 48,
        description: 'Obter pelo menos 3 cotações de fornecedores.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Aprovação',
        role: 'Gestor',
        slaHours: 24,
        description: 'Gestor aprova melhor cotação e autoriza compra.',
        canSkip: false,
        requireApproval: true,
      },
      {
        name: 'Emitir pedido',
        role: 'Compras',
        slaHours: 8,
        description: 'Gerar pedido de compra e enviar ao fornecedor.',
        canSkip: false,
        requireApproval: false,
      },
    ],
  },

  // ── RH ─────────────────────────────────────────────────
  {
    id: 'admissao-rh',
    category: 'rh',
    name: 'Admissão de Colaborador',
    description: 'Processo de admissão: documentos, cadastro e liberação.',
    icon: 'UserPlus',
    stages: [
      {
        name: 'Receber documentos',
        role: 'RH',
        slaHours: 48,
        description: 'Coletar documentos do novo colaborador.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Cadastrar no sistema',
        role: 'RH',
        slaHours: 24,
        description: 'Registrar dados pessoais, cargo e benefícios.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Liberar acesso',
        role: 'TI',
        slaHours: 8,
        description: 'Criar contas, e-mail e acesso aos sistemas.',
        canSkip: false,
        requireApproval: false,
      },
    ],
  },

  // ── Operacional ────────────────────────────────────────
  {
    id: 'processo-generico',
    category: 'operacional',
    name: 'Processo Genérico',
    description: 'Modelo em 3 etapas para qualquer fluxo operacional.',
    icon: 'Settings',
    stages: [
      {
        name: 'Etapa 1 — Início',
        role: 'Operador',
        slaHours: 8,
        description: 'Registro inicial da demanda.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Etapa 2 — Execução',
        role: 'Operador',
        slaHours: 24,
        description: 'Execução da atividade principal.',
        canSkip: false,
        requireApproval: false,
      },
      {
        name: 'Etapa 3 — Finalização',
        role: 'Gestor',
        slaHours: 8,
        description: 'Revisão e encerramento.',
        canSkip: false,
        requireApproval: true,
      },
    ],
  },
];

export function getTemplatesByCategory(category: ProcessTemplateCategory): ProcessTemplate[] {
  return PROCESS_TEMPLATES.filter((t) => t.category === category);
}

export function getTemplateById(id: string): ProcessTemplate | undefined {
  return PROCESS_TEMPLATES.find((t) => t.id === id);
}

/** Extrai roles únicos de um template */
export function getTemplateRoles(template: ProcessTemplate): string[] {
  return [...new Set(template.stages.map((s) => s.role))];
}
